import React, { useState } from 'react';
import { supabase } from '../lib/supabase';

interface TrelloConnectProps {
  onConnect: (token: string) => void;
  onSkip: () => void;
}

const TrelloConnect: React.FC<TrelloConnectProps> = ({ onConnect, onSkip }) => {
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSyncingMembers, setIsSyncingMembers] = useState(false);

  const TRELLO_OAUTH_KEY = import.meta.env.VITE_TRELLO_OAUTH_KEY;
  // Use .html to ensure static file is loaded
  const REDIRECT_URI = `${window.location.origin}/trello-callback.html`;

  const handleConnectTrello = () => {
    setIsConnecting(true);
    setError(null);

    // Trello OAuth URL
    const trelloAuthUrl = `https://trello.com/1/authorize?expiration=never&name=Meeting%20Notes%20to%20Trello&scope=read,write&response_type=token&key=${TRELLO_OAUTH_KEY}&return_url=${encodeURIComponent(REDIRECT_URI)}`;

    // Open Trello OAuth in a popup
    const popup = window.open(
      trelloAuthUrl,
      'trello-oauth',
      'width=500,height=600,scrollbars=yes,resizable=yes'
    );

    // Listen for the callback
    const handleMessage = async (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      
      if (event.data.type === 'TRELLO_OAUTH_SUCCESS') {
        const token = event.data.token;
        // Store token in Supabase DB for current user
        const { data: { user } } = await supabase.auth.getUser();
        if (!user?.id) {
          setError('Not authenticated. Please sign in again.');
          setIsConnecting(false);
          popup?.close();
          window.removeEventListener('message', handleMessage);
          return;
        }
        // Upsert token in user_trello_tokens
        const { data, error: upsertError } = await supabase
          .from('user_trello_tokens')
          .upsert({
            user_id: user.id,
            access_token: token,
            updated_at: new Date().toISOString(),
          })
          .select(); // Add select to see what was inserted
        
        if (upsertError) {
          console.error('Database error details:', upsertError);
          setError(`Failed to save Trello token: ${upsertError.message}`);
          setIsConnecting(false);
          popup?.close();
          window.removeEventListener('message', handleMessage);
          return;
        }
        
        // Before calling onConnect, let's sync Trello members
        // We need to import trelloService and set the token first
        const { trelloService } = await import('../lib/trello');
        trelloService.setToken(token);
        
        onConnect(token);
        setIsConnecting(false);
        popup?.close();
        window.removeEventListener('message', handleMessage);
      } else if (event.data.type === 'TRELLO_OAUTH_ERROR') {
        setError('Failed to connect to Trello. Please try again.');
        setIsConnecting(false);
        popup?.close();
        window.removeEventListener('message', handleMessage);
      }
    };

    window.addEventListener('message', handleMessage);

    // Check if popup was closed
    const checkClosed = setInterval(() => {
      if (popup?.closed) {
        clearInterval(checkClosed);
        setIsConnecting(false);
        window.removeEventListener('message', handleMessage);
      }
    }, 1000);
  };

  return (
    <div className="max-w-2xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">
          🔗 Connect Your Trello Account
        </h2>
        <p className="text-gray-600 mb-6">
          To create cards in your Trello boards, we need to connect to your Trello account. 
          This is secure and only allows us to create cards on your behalf.
        </p>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
        <h3 className="font-semibold text-blue-900 mb-4">🎯 What we can do:</h3>
        <ul className="space-y-2 text-blue-800">
          <li className="flex items-center">
            <span className="text-green-500 mr-2">✅</span>
            Create cards in your boards
          </li>
          <li className="flex items-center">
            <span className="text-green-500 mr-2">✅</span>
            Add descriptions and labels
          </li>
          <li className="flex items-center">
            <span className="text-green-500 mr-2">✅</span>
            Set due dates and assignees
          </li>
        </ul>
      </div>

      <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
        <h3 className="font-semibold text-red-900 mb-4">🚫 What we cannot do:</h3>
        <ul className="space-y-2 text-red-800">
          <li className="flex items-center">
            <span className="text-red-500 mr-2">❌</span>
            Access your personal data
          </li>
          <li className="flex items-center">
            <span className="text-red-500 mr-2">❌</span>
            Delete or modify existing cards
          </li>
          <li className="flex items-center">
            <span className="text-red-500 mr-2">❌</span>
            Access your account settings
          </li>
        </ul>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-4">
        <button
          onClick={handleConnectTrello}
          disabled={isConnecting || isSyncingMembers}
          className="flex-1 btn btn-primary flex items-center justify-center"
        >
          {isSyncingMembers ? (
            <>
              <div className="loading-spinner mr-2"></div>
              Syncing team members...
            </>
          ) : isConnecting ? (
            <>
              <div className="loading-spinner mr-2"></div>
              Connecting...
            </>
          ) : (
            <>
              <span className="mr-2">🔗</span>
              Connect Trello Account
            </>
          )}
        </button>
        
        <button
          onClick={onSkip}
          className="btn btn-outline"
        >
          Skip for now
        </button>
      </div>

      <div className="mt-6 text-center">
        <p className="text-sm text-gray-500">
          You can connect your Trello account later in the settings.
        </p>
      </div>
    </div>
  );
};

export default TrelloConnect; 